import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import {
  Input,
  Button,
  Field,
  Table,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableBody,
  TableCell,
  TableCellLayout,
  Dropdown,
  Option,
  Spinner,
  Checkbox,
} from "@fluentui/react-components";
import { Add20Regular, Delete20Regular, Add20Filled, Edit20Filled, Dismiss20Regular, Save20Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";

import SearchResourceInGraph from "../GenericComponent/SearchResourceInGraph";
import { getAccessToken } from "../../Services/GraphServices";
import { GetGroup, AddGroup, UpdateGroup, getAllGroups } from "../../Services/HeedifyServices";
import "../CSS/GroupForm.css";
import { useAbility } from "../../Contexts/AbilityContext";
import { useNotification } from '../GenericComponent/NotificationSystem';

export default function GroupForm({ ACTIVE_TENANT, SCOPES, setGroups }) {
  const { t } = useTranslation();
  const { groupId, scopeParam } = useParams(); // from route
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const navigate = useNavigate();
  let userId = null;
  if (activeAccount) {
    userId = activeAccount.localAccountId;
  }

  // Determine mode based on groupId
  const isCreateMode = groupId === "newGroup";
  const isEditMode = !isCreateMode;

  const [groupData, setGroupData] = useState({
    PartitionKey: ACTIVE_TENANT,
    groupName: "",
    scope: "",
    agentsIds: [],
    agentDetails: {},
    // Défaut à la création : les superviseurs peuvent écouter les appels du groupe.
    supervisorListenToCalls: true,
  });
  const [originalGroupData, setOriginalGroupData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [errors, setErrors] = useState({});
  const [formValid, setFormValid] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [initialScope, setInitialScope] = useState(null); // Pour suivre le scope initial en mode édition
  const [agentsByScope, setAgentsByScope] = useState({}); // Sauvegarder les agents par scope
  
  // 🔐 Ability pour permissions
  const ability = useAbility();
  const { showSuccess, showError } = useNotification();
  

  // ⚡ Calculer et ajouter permissions dans groupData à chaque changement de scope
  useEffect(() => {
    if (!ability || !groupData.scope) return;

    const scope = groupData.scope;
    const calculatedPermissions = {
      // `main` manquait : seule la liste d'agents portait ses permissions. Sans lui, un
      // utilisateur en LECTURE (rôle partenaire, ou tout rôle avec Read sans Update) ouvrait un
      // formulaire pleinement modifiable, avec un bouton Enregistrer actif qui finissait en 403.
      main: {
        canCreate: ability.can("create", `Group.main@${scope}`),
        canRead: ability.can("read", `Group.main@${scope}`),
        canUpdate: ability.can("update", `Group.main@${scope}`),
        canDelete: ability.can("delete", `Group.main@${scope}`),
      },
      agentList: {
        canCreate: ability.can("create", `Group.Agents@${scope}`),
        canRead: ability.can("read", `Group.Agents@${scope}`),
        canUpdate: ability.can("update", `Group.Agents@${scope}`),
        canDelete: ability.can("delete", `Group.Agents@${scope}`),
      }
    };
    console.log("Calculated permissions for scope", scope, calculatedPermissions);
    setGroupData(prev => ({ ...prev, permissions: calculatedPermissions }));
  }, [ability, groupData.scope]);

  // 🔄 Réinitialiser la liste des agents lors du changement de scope
  useEffect(() => {
    if (!groupData.scope) return;
    
    const currentScope = groupData.scope;
    
    // Restaurer les agents du scope sélectionné (s'ils existent)
    if (agentsByScope[currentScope]) {
      setGroupData(prev => ({
        ...prev,
        agentsIds: agentsByScope[currentScope].agentsIds || [],
        agentDetails: agentsByScope[currentScope].agentDetails || {}
      }));
    } else {
      // Nouveau scope : vider les agents
      setGroupData(prev => ({
        ...prev,
        agentsIds: [],
        agentDetails: {}
      }));
    }
  }, [groupData.scope]);
  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!groupData.groupName)
      newErrors.groupName = t("THIS_FIELD_IS_REQUIRED");
    if (!groupData.scope)
      newErrors.scope = t("THIS_FIELD_IS_REQUIRED");

    setErrors(newErrors);

    // Retourne true si aucun champ vide
    const isValid = Object.keys(newErrors).length === 0;
    setFormValid(isValid);
    return isValid;
  };

  // Validate on field changes
  useEffect(() => {
    if (!isLoading) {
      validateForm();
    }
  }, [groupData.groupName, groupData.scope, isLoading]);

  // Function to refresh groups list
  const refreshGroupsList = (token) => {
    const scopeNames = Object.keys(SCOPES || {});
    const groupsPromises = scopeNames.map((s) =>
      getAllGroups(ACTIVE_TENANT, s, userId, token)
    );
    Promise.all(groupsPromises).then((data) => setGroups(data.flat()));
  };
  // Load existing group for edit
  useEffect(() => {
    const loadGroup = async () => {
      // In create mode, just stop loading
      if (isCreateMode) {
        setIsLoading(false);
        return;
      }

      // In edit mode, fetch the group data
      setIsLoading(true);
      try {
        const token = await getAccessToken(instance, activeAccount);
        const group = await GetGroup(
          ACTIVE_TENANT,
          scopeParam,
          groupId,
          userId,
          token
        );
        if (group) {
          const loadedScope = group.scope || "";
          const loadedGroupData = {
            PartitionKey: group.PartitionKey || ACTIVE_TENANT,
            id: group.id || "",
            groupName: group.groupName || "",
            scope: loadedScope,
            agentsIds: group.agentsIds || [],
            agentDetails: group.agentDetails || {},
            createdAt: group.createdAt,
            createdBy: group.createdBy,
            updatedAt: group.updatedAt,
            updatedBy: group.updatedBy,
          };
          setGroupData(loadedGroupData);
          // Sauvegarder l'objet original pour détecter les changements
          setOriginalGroupData(JSON.parse(JSON.stringify(loadedGroupData)));
          // Sauvegarder le scope initial et ses agents
          setInitialScope(loadedScope);
          setAgentsByScope({
            [loadedScope]: {
              agentsIds: group.agentsIds || [],
              agentDetails: group.agentDetails || {}
            }
          });
        }
        if (!group) {
          // Même mode de panne que les files : sans ce garde, l'écran restait sur l'objet vide et
          // affichait un formulaire de création vierge pour un groupe qu'on venait d'ouvrir.
          console.error("Impossible de charger le groupe (accès refusé ou introuvable) :", groupId);
          setLoadError(true);
        }
      } catch (err) {
        console.error("Error loading group:", err);
        setLoadError(true);
      } finally {
        setIsLoading(false);
      }
    };
    loadGroup();
  }, [groupId, ACTIVE_TENANT, instance, activeAccount, scopeParam, isCreateMode]);

  const updateField = (field, value) => {
    // Si on change le scope, sauvegarder les agents du scope actuel avant le changement
    if (field === 'scope' && groupData.scope && groupData.scope !== value) {
      setAgentsByScope(prev => ({
        ...prev,
        [groupData.scope]: {
          agentsIds: groupData.agentsIds,
          agentDetails: groupData.agentDetails
        }
      }));
    }
    setGroupData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddAgent = (user) => {
    if (!user?.id) return;
    setGroupData((prev) => {
      if (prev.agentsIds.includes(user.id)) return prev;
      return {
        ...prev,
        agentsIds: [...prev.agentsIds, user.id],
        agentDetails: {
          ...prev.agentDetails,
          [user.id]: {
            displayName: user.displayName || user.header,
            email: user.email,
            isSupervisor: false,
          },
        },
      };
    });
  };

  const toggleSupervisor = (agentId) => {
    setGroupData((prev) => ({
      ...prev,
      agentDetails: {
        ...prev.agentDetails,
        [agentId]: {
          ...prev.agentDetails[agentId],
          isSupervisor: !prev.agentDetails[agentId]?.isSupervisor,
        },
      },
    }));
  };

  const handleRemoveAgent = (id) => {
    setGroupData((prev) => {
      const updatedIds = prev.agentsIds.filter((a) => a !== id);
      const updatedDetails = { ...prev.agentDetails };
      delete updatedDetails[id];
      return { ...prev, agentsIds: updatedIds, agentDetails: updatedDetails };
    });
  };
  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    // En mode édition, vérifier si des changements ont été effectués
    if (isEditMode && originalGroupData) {
      const excludeFields = ['permissions', 'updatedAt', 'updatedBy'];
      
      const cleanCurrentData = Object.keys(groupData)
        .filter(key => !excludeFields.includes(key))
        .reduce((obj, key) => {
          obj[key] = groupData[key];
          return obj;
        }, {});
      
      const cleanOriginalData = Object.keys(originalGroupData)
        .filter(key => !excludeFields.includes(key))
        .reduce((obj, key) => {
          obj[key] = originalGroupData[key];
          return obj;
        }, {});
      
      const hasChanges = JSON.stringify(cleanCurrentData) !== JSON.stringify(cleanOriginalData);
      if (!hasChanges) {
        showSuccess("Aucun changement à enregistrer");
        navigate("/groups");
        return;
      }
    }

    setSaving(true);
    
    return getAccessToken(instance, activeAccount)
      .then((token) => {

        // Supprimer permissions avant envoi
        const { permissions: _, updatedAt, updatedBy, ...groupDataToSave } = groupData;

        // Ajouter createdAt/createdBy en mode création
        if (isCreateMode) {
          groupDataToSave.createdAt = new Date().toISOString();
          groupDataToSave.createdBy = userId;
        } else {
          // En mode édition, préserver createdAt/createdBy et ajouter updatedAt/updatedBy
          if (groupData.createdAt) {
            groupDataToSave.createdAt = groupData.createdAt;
          }
          if (groupData.createdBy) {
            groupDataToSave.createdBy = groupData.createdBy;
          }
          // Ajouter updatedAt et updatedBy uniquement lors d'un update réel
          groupDataToSave.updatedAt = new Date().toISOString();
          groupDataToSave.updatedBy = userId;
        }

        if (isEditMode) {
          // Update existing group
          return UpdateGroup(
            ACTIVE_TENANT,
            groupData.scope,
            userId,
            groupData.id,
            groupDataToSave,
            token
          ).then((response) => {
            if (response?.success === true && response?.message === 'Updated') {
              refreshGroupsList(token);
              showSuccess(t("GROUP_UPDATED_SUCCESS"));
              navigate("/groups");
            } else {
              const errorMsg = response?.message || "Erreur inconnue";
              console.error("Échec mise à jour groupe:", response);
              showError(t("GROUP_UPDATE_FAILED"), errorMsg);
            }
          });
        } else {
          // Create new group
          return AddGroup(
            ACTIVE_TENANT,
            groupData.scope,
            userId,
            groupDataToSave,
            token
          ).then((response) => {
            if (response?.success === true && response?.message === 'Created') {
              refreshGroupsList(token);
              showSuccess(t("GROUP_CREATED_SUCCESS"));
              navigate("/groups");
            } else {
              const errorMsg = response?.message || "Erreur inconnue";
              console.error("Échec création groupe:", response);
              showError(t("GROUP_CREATION_FAILED"), errorMsg);
            }
          });
        }
      })
      .catch((err) => {
        console.error("Error saving group:", err);
        showError(isEditMode ? t("GROUP_UPDATE_ERROR") : t("GROUP_CREATION_ERROR"), err?.message);
      })
      .finally(() => {
        setSaving(false);
      });
  };


  // LECTURE SEULE : on CONSULTE la configuration sans pouvoir l'enregistrer.
  //
  // Le portail plaçait la consultation derrière le bouton « Éditer » de la liste, lui-même gardé
  // par `canUpdate` : un utilisateur en lecture ne pouvait donc RIEN consulter. C'est ce que vivait
  // le revendeur — il voyait la ligne du groupe et rien de son contenu.
  //
  // La permission d'écran est désormais `canRead` (côté liste) et c'est l'ENREGISTREMENT qui porte
  // `canUpdate`. En création, `canCreate` gouverne — on ne peut de toute façon pas atteindre cet
  // écran sans lui, le bouton « Add New Group » y veille.
  // Refus par DÉFAUT : `!== true`, pas `=== false`. Tant que les permissions ne sont pas
  // calculées — donnée pas encore arrivée, scope absent, `ability` pas prêt — la valeur est
  // `undefined`, et `undefined === false` vaut FAUX : l'écran se croyait modifiable pendant
  // ce laps de temps, et pour de bon si le scope manquait. En cas de doute, on refuse.
  const readOnly = isEditMode && groupData.permissions?.main?.canUpdate !== true;

  if (loadError) {
    return (
      <div className="group-form-root" style={{ padding: "2rem", textAlign: "center" }}>
        <h3>{t("GROUP_LOAD_FAILED")}</h3>
        <p style={{ opacity: 0.7 }}>{groupId}</p>
        <Button appearance="primary" onClick={() => navigate("/groups")}>
          {t("BACK_TO_LIST")}
        </Button>
      </div>
    );
  }

  return (
    <div className="group-form-root">

      <div className="group-form-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <Button
          icon={<Dismiss20Regular />}
          appearance="secondary"
          onClick={() => navigate("/groups")}
          disabled={isLoading}
        >
          {readOnly ? t("CLOSE") : t("CANCEL")}
        </Button>
        {/* Masqué et non désactivé : un bouton grisé laisse croire qu'il manque une donnée à
            remplir, alors qu'il ne s'agit pas d'un formulaire incomplet mais d'un droit absent. */}
        {!readOnly && (
          <Button
            appearance="primary"
            className="group-form-button-submit"
            icon={saving ? <Spinner size="tiny" /> : <Save20Regular />}
            onClick={handleSubmit}
            disabled={saving || isLoading || !formValid}
          >
            {isEditMode ? t("SAVE") : t("ADD")}
          </Button>
        )}
      </div>
      <div className="group-form-container" style={{ marginLeft: '8rem', marginRight: '8rem' }}>
        {/* Group Name */}
        <div className="group-form-group">
          <Field
            label={t("GROUP_NAME")}
            className="group-form-field"
            required
            validationState={errors.groupName ? "error" : "none"}
            validationMessage={errors.groupName}
          >
            <Input className="group-form-input" placeholder={t("ENTER_GROUP_NAME")} value={groupData.groupName} onChange={(e, data) => updateField("groupName", data.value)} disabled={readOnly} />
          </Field>
        </div>

        {/* Scope Selector */}
        <div className="group-form-group">
          <Field
            label={t("SCOPE")}
            className="group-form-field"
            required
            validationState={errors.scope ? "error" : "none"}
            validationMessage={errors.scope}
          >
            <Dropdown
              placeholder={t("SELECT_SCOPE")}
              value={groupData.scope}
              selectedOptions={[groupData.scope]}
              onOptionSelect={(e, data) => updateField("scope", data.optionValue)}
              className="group-scope"
              disabled={readOnly}
            >
              {Object.keys(SCOPES || {}).map((scope) => (
                <Option key={scope} value={scope}>
                  {scope}
                </Option>
              ))}
            </Dropdown>
          </Field>
        </div>

        {/* Add Agents */}

        <div className="group-form-agents-section">
          <h3>{t("AGENT_LIST")}</h3>
         {(isEditMode ? groupData.permissions?.agentList?.canUpdate : groupData.permissions?.agentList?.canCreate) && (
            <Button className="group-form-button-add" appearance="primary" icon={<Add20Regular />} onClick={() => setShowUserSearch(true)} disabled={isLoading}>
            </Button>
          )}
          {showUserSearch && (
            <div className="group-form-search-container">
              <SearchResourceInGraph
                onSelectUser={(user) => {
                  handleAddAgent(user);
                  setShowUserSearch(false);
                }}
                onCancel={() => setShowUserSearch(false)}
                disabled={isLoading}
              />
            </div>
          )}

          {/* Agents Table */}
          {groupData.permissions?.agentList?.canRead && (<div className="group-form-table-container">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>{t("NAME")}</TableHeaderCell>
                  <TableHeaderCell>{t("EMAIL")}</TableHeaderCell>
                  <TableHeaderCell>{t("SUPERVISOR")}</TableHeaderCell>
                  <TableHeaderCell>{t("ACTION")}</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groupData.agentsIds.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <TableCellLayout className="group-form-empty-state">{t("NO_AGENTS_ADDED")}</TableCellLayout>
                    </TableCell>
                  </TableRow>
                ) : (
                  groupData.agentsIds.map((id) => {
                    const agent = groupData.agentDetails[id];
                    return (
                      <TableRow key={id}>
                        <TableCell><TableCellLayout>{agent?.displayName}</TableCellLayout></TableCell>
                        <TableCell><TableCellLayout>{agent?.email}</TableCellLayout></TableCell>
                        <TableCell>
                          <Checkbox
                            checked={agent?.isSupervisor || false}
                            onChange={() => toggleSupervisor(id)}
                            disabled={!groupData.permissions?.agentList?.canUpdate}
                          />
                        </TableCell>
                        <TableCell>
                          <Button icon={<Delete20Regular />} appearance="subtle" disabled={!groupData.permissions?.agentList?.canDelete} onClick={() => handleRemoveAgent(id)} />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>)}
        </div>


        {/* Action Buttons */}
      </div>


    </div>
  );
}
